
//----------------------------------------------------------
// Copyright (C) Microsoft Corporation. All rights reserved.
//----------------------------------------------------------
/*
*   TFS.Extension
*
*   This library should be referenced by TFS extension/integrations.
*   The HTML content downloaded in a TFS extension integration frames 
*   should reference this file and invoke TFS.Extension.ready() when
*   the extension has been loaded. This library allows the extension's
*   client code to interact with the TFS page that is hosting the
*   extension integration.
*
*   You can upload an extension to TFS by visiting the "extensions"
*   hub in the Administration area of your root TFS url (Control Panel).
*
*   The Extension file to upload is an XML file in the following format:
*
*   <?xml version="1.0" encoding="utf-8" ?>
*   <Extension 
*       name="My Extension Name"
*       id="{70D19AD3-93FA-437B-8CCE-CD80AA405F68}"
*       version="1.0" 
*       min-host-version="11">
*
*      <Description>Extension description</Description>
* 
*      <Integrations>
*          <Frame end-point="tfs.source-control.file-viewer" url="http://server/Editor/Edit">
*              <Properties>
*                  <Property name="propertyName" value="propertyValue"/>
*              </Properties>
*          </Frame>
*      </Integrations>
*   </Extension>
*
*/

/**
 * Encapsulates Legacy TFS Extension code into a class to provide multiple instances within the same page as a trusted, built-in extension.
 * The original iframe model is also still supported via the module-level functions that delegate to the singleton instance.
 */
export class Extension {

    private _currentRequestId = 0;
    private _currentConfiguration: any = {};
    private _messageListeners = [];

    // Built-in legacy extensions (Monaco CodeEditor) can alternatively be hosted within the same page instead of an iframe.
    private _isBuiltIn = false;         // true, if this is hosted in a div as a built-in extension vs in an iframe.
    private _$messagesOut: JQuery;      // the element used for outgoing messages in lieu of posting XDM messages.
    private _hostId: string = "";       // The owning host/parent id required to ensure specificity when multiple entensions exist

    private _internalActionIds = {
        LOADED_ACTION_ID: "extension-loaded"
    };

    /**
     * Initializes XDM communication when hosting in an iframe.
     */
    public initializeIframeExtension() {
        this._initializeMessageReceiver();
        this.sendMessage(_internalActionIds.LOADED_ACTION_ID);
    }

    /**
     * Gets the owning host/parent id required to ensure specificity when multiple entensions exist
     */
    public getHostId() {
        return this._hostId;
    }

    public addMessageListener(actionId, listener?) {
        /// <summary>
        ///     Adds a listener for messages from the TFS host window (or messages in for a built-in extension)
        /// </summary>
        /// <param name="actionId" type="String" optional="true">
        ///     (Optional) Id of the extension action that this listener is appropriate for.
        ///     Set to null or omit this parameter to add a listener for all action types.
        /// </param>
        /// <param name="listener" type="Function">
        ///     Callback function called whenever a message is received: Function(message)
        /// </param>

        if (listener === undefined && typeof actionId === "function") {
            listener = actionId;
            actionId = null;
        }

        this._messageListeners.push({
            actionId: actionId,
            listener: listener
        });
    }

    public removeMessageListener(listener) {
        /// <summary>
        ///     Removes a message listener
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Message listener to remove
        /// </param>

        var newListeners = [];
        $.each(this._messageListeners, (i, messageListener) => {
            if (messageListener.listener !== listener) {
                newListeners.push(messageListener);
            }
        });
        this._messageListeners = newListeners;
    }

    private _sendMessage(actionId, actionData?, requestId?, callback?) {
        /// <summary>
        ///     Post an XDM message to the TFS host window (or messages out for a built-in extension)
        /// </summary>
        /// <param name="actionId" type="String">
        ///     Id of the extension action
        /// </param>
        /// <param name="actionData" type="Object">
        ///     Action-specific data
        /// </param>
        /// <param name="requestId" type="Object">
        ///     The request id to send in this message
        /// </param>
        /// <param name="callback" type="IResultCallback" optional="true">
        ///     (Optional). If provided, wait for a response message from the host 
        ///     with the same request id that we send here. The provied callback will
        ///     be invoked with the response message as a single argument
        /// </param>

        var listener = null;

        if (typeof callback === "function") {
            listener = (message) => {
                if (message && message.requestId === requestId) {
                    this.removeMessageListener(listener);
                    callback.call(this, message);
                }
            };
            this.addMessageListener(listener);
        }

        var message = JSON.stringify({
            actionId: actionId,
            actionData: actionData || {},
            requestId: requestId
        });

        if (this._isBuiltIn) {
            setTimeout(() => {
                this._$messagesOut.triggerHandler("message", [{ data: message }]);
            }, 0);
        }
        else {
            window.parent.postMessage(message, "*");
        }
    }

    public sendMessage(actionId, actionData?, callback?) {
        /// <summary>
        ///     Post an XDM message to the TFS host window (or messages out for a built-in extension)
        /// </summary>
        /// <param name="actionId" type="String">
        ///     Id of the extension action
        /// </param>
        /// <param name="actionData" type="Object">
        ///     Action-specific data
        /// </param>
        /// <param name="callback" type="IResultCallback" optional="true">
        ///     (Optional). If provided, wait for a response message from the host 
        ///     with the same request id that we send here. The provied callback will
        ///     be invoked with the response message as a single argument
        /// </param>
        this._sendMessage(actionId, actionData, ++this._currentRequestId, callback);
    }

    public sendResponse(requestId, actionId, actionData) {
        /// <summary>
        ///     Post an XDM message to the TFS host window (or messages out for a built-in extension) in response to
        ///     a request from the host window
        /// </summary>
        /// <param name="requestId" type="Object">
        ///     The request id from the original request sent from the
        ///     host window that we are replying to here
        /// </param>
        /// <param name="actionId" type="String">
        ///     Id of the extension action
        /// </param>
        /// <param name="actionData" type="Object">
        ///     Action-specific data
        /// </param>
        this._sendMessage(actionId, actionData, requestId, null);
    }

    public ready() {
        /// <summary>
        ///     Notifies the TFS host window that this extension
        ///     point is ready. No notifications will be sent to
        ///     this extension until this method is called.
        /// </summary>
        this.sendMessage(ActionIds.EXTENSION_READY_ACTION_ID);
    }

    public getConfiguration() {
        /// <summary>
        ///     Get this extension point's current configuration JSON data
        /// </summary>
        return this._currentConfiguration;
    }

    public sendLoadError(errorMessage) {
        /// <summary>
        ///     Tell the host that a load error occurred in this extension.
        /// </summary>
        /// <param name="errorMessage" type="String">
        ///     Detailed message about the error.
        /// </param>
        this.sendMessage(ActionIds.LOAD_ERROR_ACTION_ID, {
            errorMessage: errorMessage
        });
    }

    public onConfigurationChanged(listener) {
        /// <summary>
        ///     Adds a listener to be called when the integration's configuration is changed.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the configuration is changed: Function(setConfigurationMessage).
        /// </param>
        this.addMessageListener(ActionIds.SET_CONFIGURATION_ACTION_ID, listener);
    }

    private _handleMessageReceived(event: any) {
        var data;
        if (event && event.data) {

            try {
                data = JSON.parse(event.data);
            }
            catch (ex) {
                if (window.console) {
                    window.console.log("Failed to parse JSON data: " + event.data);
                }
            }

            if (data) {
                if (data.actionId === ActionIds.SET_CONFIGURATION_ACTION_ID) {
                    this._currentConfiguration = data.actionData || {};
                }

                $.each(this._messageListeners, (i, messageListener) => {
                    if (!messageListener.actionId || messageListener.actionId === data.actionId) {
                        messageListener.listener.call(this, data);
                    }
                });
            }
        }
    }

    private _initializeMessageReceiver() {
        this._isBuiltIn = false;

        if (window.addEventListener) {
            window.addEventListener("message", (event) => {
                this._handleMessageReceived(event);
            });
        }
        else {
            // IE8
            (<any>window).attachEvent("onmessage", (event) => {
                this._handleMessageReceived(event);
            });
        }
    }

    /**
     * Initialize to allow messages from any HTML element within the current page instead of the original iframe XDM model
     */
    protected _initializeBuiltInExtension($messagesIn: JQuery, $messagesOut: JQuery, hostId: string) {
        this._isBuiltIn = true;
        this._$messagesOut = $messagesOut;
        this._currentRequestId = 0;
        this._currentConfiguration = {};
        this._messageListeners = [];
        this._hostId = hostId;

        // Remove the window (iframe) event listener that was added upon script loading.
        if (_moduleExtension.getHostId() === "") {
            window.removeEventListener("message", (event) => {
                _moduleExtension._handleMessageReceived(event);
            });
        }

        // Add a listener for a non-iframe extension.  We don't support IE8 anymore, so no need for the legacy attachEvent.
        $messagesIn.on("message", (event: JQueryEventObject, params: any) => {
            this._handleMessageReceived(params);
        });

        // For common cases where there is only one code editor or diff editor on a page, we can refer to it at the module level for back-compat with tests, etc.
        _moduleExtension = <any>this;

        this.sendMessage(_internalActionIds.LOADED_ACTION_ID);
    }
}

export var ActionIds = {
    EXTENSION_READY_ACTION_ID: "extension-ready",
    SET_CONFIGURATION_ACTION_ID: "set-configuration",
    LOAD_ERROR_ACTION_ID: "extension-load-error"
};

var _internalActionIds = {
    LOADED_ACTION_ID: "extension-loaded"
};

export function addMessageListener(actionId, listener?) {
    /// <summary>
    ///     Adds a listener for messages from the TFS host window (or messages in for a built-in extension)
    /// </summary>
    /// <param name="actionId" type="String" optional="true">
    ///     (Optional) Id of the extension action that this listener is appropriate for.
    ///     Set to null or omit this parameter to add a listener for all action types.
    /// </param>
    /// <param name="listener" type="Function">
    ///     Callback function called whenever a message is received: Function(message)
    /// </param>
    _moduleExtension.addMessageListener(actionId, listener);
}

export function removeMessageListener(listener) {
    /// <summary>
    ///     Removes a message listener
    /// </summary>
    /// <param name="listener" type="Function">
    ///     Message listener to remove
    /// </param>
    _moduleExtension.removeMessageListener(listener);
}

export function sendMessage(actionId, actionData?, callback?) {
    /// <summary>
    ///     Post an XDM message to the TFS host window (or messages out for a built-in extension)
    /// </summary>
    /// <param name="actionId" type="String">
    ///     Id of the extension action
    /// </param>
    /// <param name="actionData" type="Object">
    ///     Action-specific data
    /// </param>
    /// <param name="callback" type="IResultCallback" optional="true">
    ///     (Optional). If provided, wait for a response message from the host 
    ///     with the same request id that we send here. The provied callback will
    ///     be invoked with the response message as a single argument
    /// </param>
    _moduleExtension.sendMessage(actionId, actionData, callback);
}

export function sendResponse(requestId, actionId, actionData) {
    /// <summary>
    ///     Post an XDM message to the TFS host window (or messages out for a built-in extension) in response to
    ///     a request from the host window
    /// </summary>
    /// <param name="requestId" type="Object">
    ///     The request id from the original request sent from the
    ///     host window that we are replying to here
    /// </param>
    /// <param name="actionId" type="String">
    ///     Id of the extension action
    /// </param>
    /// <param name="actionData" type="Object">
    ///     Action-specific data
    /// </param>
    _moduleExtension.sendResponse(requestId, actionId, actionData);
}

export function ready() {
    /// <summary>
    ///     Notifies the TFS host window that this extension
    ///     point is ready. No notifications will be sent to
    ///     this extension until this method is called.
    /// </summary>
    _moduleExtension.ready();
}

export function getConfiguration() {
    /// <summary>
    ///     Get this extension point's current configuration JSON data
    /// </summary>
    return _moduleExtension.getConfiguration();
}

export function sendLoadError(errorMessage) {
    /// <summary>
    ///     Tell the host that a load error occurred in this extension.
    /// </summary>
    /// <param name="errorMessage" type="String">
    ///     Detailed message about the error.
    /// </param>
    _moduleExtension.sendLoadError(errorMessage);
}

export function onConfigurationChanged(listener) {
    /// <summary>
    ///     Adds a listener to be called when the integration's configuration is changed.
    /// </summary>
    /// <param name="listener" type="Function">
    ///     Callback invoked when the configuration is changed: Function(setConfigurationMessage).
    /// </param>
    _moduleExtension.onConfigurationChanged(listener);
}


/**
 * Encapsulates Legacy TFS Version Control Extension code into a class to provide multiple instances within the same page as a trusted, built-in extension.
 * The original iframe model is also still supported via the module-level functions that delegate to the singleton instance.
 */
export class VersionControlExtension extends Extension {

    private _codeEditor: any; // TFS_CodeEditor.CodeEditor

    /**
     * Initialize the built-in Code Editor (Monaco) within the current page instead of the original iframe XDM model
     */
    public static createBuiltInCodeEditor($messagesIn: JQuery, $messagesOut: JQuery, hostId: string) {
        var versionControlExtension = new VersionControlExtension();
        versionControlExtension._initializeBuiltInExtension($messagesIn, $messagesOut, hostId);

        require(["BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor"], (TFS_CodeEditor) => {
            var codeEditor = new TFS_CodeEditor.CodeEditor();
            versionControlExtension._codeEditor = codeEditor;
            codeEditor.beginCreateBuiltInEditor((editor) => {
                versionControlExtension.onConfigurationChanged(() => {
                    codeEditor.setEditorConfig(editor, versionControlExtension.getConfiguration());
                });
                versionControlExtension.ready();
            }, versionControlExtension);
        });
    }

    /**
    * Initialize the built-in Diff Editor (Monaco) within the current page instead of the original iframe XDM model
    */
    public static createBuiltInDiffEditor($messagesIn: JQuery, $messagesOut: JQuery, hostId: string) {
        var versionControlExtension = new VersionControlExtension();
        versionControlExtension._initializeBuiltInExtension($messagesIn, $messagesOut, hostId);

        require(["BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor"], (TFS_CodeEditor) => {
            var codeEditor = new TFS_CodeEditor.CodeEditor();
            versionControlExtension._codeEditor = codeEditor;
            codeEditor.beginCreateBuiltInDiffEditor((editor) => {
                versionControlExtension.onConfigurationChanged(() => {
                    codeEditor.setDiffEditorConfig(editor, versionControlExtension.getConfiguration());
                });
                versionControlExtension.ready();
            }, versionControlExtension);
        });
    }

    /**
     * If used as a built-in extension, returns known codeEditor.isContentLoadInProgress().  Else, returns null if legacy iframe model.
     */
    public isContentLoadInProgress(): boolean {
        return this._codeEditor ? this._codeEditor.isContentLoadInProgress() : null;
    }

    public getItemContents(path, version, options, callback, errorCallback) {
        /// <summary>
        ///     Retrieve the contents of the specified version-controlled item
        /// </summary>
        /// <param name="path" type="String">
        ///     Required: The TFS path to the item (e.g. "$/MyProject/foo.txt")
        /// </param>
        /// <param name="version" type="Object" optional="true">
        ///     Optional: Describes the version of the item to retrieve. Can
        ///               be in one of the following forms:
        ///               
        ///     null/empty: Latest version of the file (tip)
        ///     
        ///     "T":    Latest version of the file (tip)
        ///     "PT":   Previous version of the file (tip - 1)
        ///     123:    Version of the file at changeset #123
        ///     "C123": Version of the file at changeset #123
        ///     "Sname;owner": Version of the file in shelveset "name" owned by "owner"
        /// </param>
        /// <param name="options" type="Object" optional="true">
        ///     Optional: Describes additional parameters to pass when fetching content:
        ///               
        ///     maxContentLength: The maximum length (in bytes) to return in the file content
        ///                       If the file's content exceeds this limit, the returned
        ///                       content will be truncated at this length and 
        ///                       "exceededMaxContentLength" will be set to true on the 
        ///                       returned contentInfo object.
        /// </param>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method in the form: function(contentInfo)
        ///
        ///     contentInfo has properties such as:
        ///         content: Content string or byte array
        ///         contentType: type of content
        ///         isBinary: Is this binary content (does 'content' specify a byte array)
        ///         isImage: Is this an image file content
        /// </param>
        /// <param name="errorCallback" type="IErrorCallback">
        ///     Callback method in the form: function(error)
        /// </param>

        this.sendMessage(VersionControl.ActionIds.GET_ITEM_CONTENT, {
            path: path,
            version: version,
            maxContentLength: (options && options.maxContentLength) || 0
        }, (message) => {
            var actionData = message.actionData || {};
            if (actionData.error) {
                if (errorCallback) {
                    errorCallback.call(this, actionData.error);
                }
            }
            else {
                if (callback) {
                    callback.call(this, actionData);
                }
            }
        });
    }

    public onResetEditorDiffContent(listener) {
        /// <summary>
        ///     Adds a listener to be called when the editor is either reverted from edit mode or 
        ///     an edit is committed.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when editor should be reset to pre-edit state
        /// </param>
        this.addMessageListener(VersionControl.ActionIds.RESET_EDITOR_DIFF_CONTENT, listener);
    }

    public onShowNextDifference(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's show-next-difference action has been invoked.
        ///     The diff viewer should be navigated to the next difference.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Next Difference action is invoked.
        /// </param>
        this.addMessageListener(VersionControl.ActionIds.NEXT_DIFFERENCE, listener);
    }

    public onShowPreviousDifference(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's show-previous-difference action has been invoked.
        ///     The diff viewer should be navigated to the previous difference.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Prev Difference action is invoked.
        /// </param>
        this.addMessageListener(VersionControl.ActionIds.PREVIOUS_DIFFERENCE, listener);
    }

    public onNavigateToPosition(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's set-position action has been invoked.
        ///     The viewer should be navigated to the given position.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Set Position action is invoked.
        /// </param>
        this.addMessageListener(VersionControl.ActionIds.NAVIGATE_TO_POSITION, listener);
    }

    public sendCurrentDiffStatus(prevDiffEnabled, nextDiffEnabled) {
        /// <summary>
        ///     Sends updated diff status information to the TFS host window
        /// </summary>
        /// <param name="prevDiffEnabled" type="Boolean">
        ///     Should the previous difference button be enabled?
        /// </param>
        /// <param name="nextDiffEnabled" type="Boolean">
        ///     Should the next difference button be enabled?
        /// </param>

        this.sendMessage(VersionControl.ActionIds.UPDATE_DIFF_STATUS, {
            prevDiffEnabled: prevDiffEnabled,
            nextDiffEnabled: nextDiffEnabled
        });
    }

    public sendDiffLines(diffLines: number[]) {
        /// <summary>
        ///     Sends updated diff ranges information to the TFS host window
        /// </summary>
        /// <param name="diffLines" type="number[]">
        ///     The starting line of each diff range
        /// </param>

        this.sendMessage(VersionControl.ActionIds.UPDATE_DIFF_LINES, {
            diffLines,
        });
    }

    public sendEditorContentChangedEvent(isLoading: boolean, content: string) {
        /// <summary>
        ///     Send an editor content-changed message to the TFS host
        /// </summary>
        this.sendMessage(VersionControl.ActionIds.EDITOR_CONTENT_CHANGED, {
            isLoading,
            content,
        });
    }

    public sendEditorDirtyStateChangedEvent(isDirty: boolean) {
        /// <summary>
        ///     Send an editor dirty state change message to the TFS host
        /// </summary>
        this.sendMessage(VersionControl.ActionIds.EDITOR_DIRTY_STATE_CHANGED, {
            isDirty: isDirty
        });
    }

    public sendEditorContent(content, requestId) {
        /// <summary>
        ///     Send the specified editor content to the TFS host
        /// </summary>
        /// <param name="content" type="String">
        ///     Current editor content
        /// </param>
        /// <param name="requestId" type="Object">
        ///     Id of the original request (from the Host page) for the
        ///     content. This allows the Host to match up this response
        ///     with its original request.
        /// </param>
        this.sendResponse(requestId, VersionControl.ActionIds.GET_EDITOR_CONTENT, {
            content: content
        });
    }

    /**
     * Send an editor preferences changed message to the TFS host
     */
    public sendEditorPreferencesChangedEvent(preferences: any) {
        this.sendMessage(VersionControl.ActionIds.EDITOR_PREFERENCES_CHANGED, {
            preferences: preferences
        });
    }
}

export module VersionControl {
    /// <summary>
    ///     Helpers to obtain TFS Version Control information from the host
    /// </summary>

    export var ActionIds = {
        GET_ITEM_CONTENT: "vc-get-content",
        NEXT_DIFFERENCE: "vc-next-diff",
        PREVIOUS_DIFFERENCE: "vc-prev-diff",
        NAVIGATE_TO_POSITION: "vc-navigate-to-position",
        UPDATE_DIFF_STATUS: "vc-update-diff-status",
        UPDATE_DIFF_LINES: "vc-update-diff-lines",
        EDITOR_CONTENT_CHANGED: "vc-editor-content-changed",
        EDITOR_DIRTY_STATE_CHANGED: "vc-editor-dirty-state-changed",
        RESET_EDITOR_DIFF_CONTENT: "vc-reset-editor-diff-content",
        GET_EDITOR_CONTENT: "vc-get-editor-content",
        GET_SCROLL_POSITION: "vc-editor-get-scroll-position",
        SET_SCROLL_POSITION: "vc-editor-set-scroll-position",
        SCROLL_POSITION_CHANGED: "vc-editor-scroll-position-changed",
        GET_SELECTION: "vc-editor-get-selection",
        SELECTION_CHANGED: "vc-editor-selection-changed",
        REFRESH_LAYOUT: "vc-editor-refresh-layout",
        DISCUSSION_THREAD_UPDATED: "vc-discussion-thread-updated",
        EDITOR_KEY_PRESSED: "vc-editor-key-pressed",
        EDITOR_CONTEXT_MENU_ITEM_CLICKED: "vc-editor-context-menu-item-clicked",
        EDITOR_ADD_CONTEXT_MENU_ITEM: "vc-editor-add-context-menu-item",
        EDITOR_CREATED: "vc-editor-created",
        EDITOR_ESCAPE_EDIT: "vc-editor-escape-edit",
        EDITOR_PREFERENCES_CHANGED: "vc-editor-preferences-changed",
    };

    export var IntegrationPointIds = {
        FILE_VIEWER: "tfs.source-control.file-viewer",
        DIFF_VIEWER: "tfs.source-control.diff-viewer"
    };

    export function getItemContents(path, version, options, callback, errorCallback) {
        /// <summary>
        ///     Retrieve the contents of the specified version-controlled item
        /// </summary>
        /// <param name="path" type="String">
        ///     Required: The TFS path to the item (e.g. "$/MyProject/foo.txt")
        /// </param>
        /// <param name="version" type="Object" optional="true">
        ///     Optional: Describes the version of the item to retrieve. Can
        ///               be in one of the following forms:
        ///               
        ///     null/empty: Latest version of the file (tip)
        ///     
        ///     "T":    Latest version of the file (tip)
        ///     "PT":   Previous version of the file (tip - 1)
        ///     123:    Version of the file at changeset #123
        ///     "C123": Version of the file at changeset #123
        ///     "Sname;owner": Version of the file in shelveset "name" owned by "owner"
        /// </param>
        /// <param name="options" type="Object" optional="true">
        ///     Optional: Describes additional parameters to pass when fetching content:
        ///               
        ///     maxContentLength: The maximum length (in bytes) to return in the file content
        ///                       If the file's content exceeds this limit, the returned
        ///                       content will be truncated at this length and 
        ///                       "exceededMaxContentLength" will be set to true on the 
        ///                       returned contentInfo object.
        /// </param>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method in the form: function(contentInfo)
        ///
        ///     contentInfo has properties such as:
        ///         content: Content string or byte array
        ///         contentType: type of content
        ///         isBinary: Is this binary content (does 'content' specify a byte array)
        ///         isImage: Is this an image file content
        /// </param>
        /// <param name="errorCallback" type="IErrorCallback">
        ///     Callback method in the form: function(error)
        /// </param>
        _moduleExtension.getItemContents(path, version, options, callback, errorCallback);
    }

    export function onResetEditorDiffContent(listener) {
        /// <summary>
        ///     Adds a listener to be called when the editor is either reverted from edit mode or 
        ///     an edit is committed.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when editor should be reset to pre-edit state
        /// </param>
        _moduleExtension.onResetEditorDiffContent(listener);
    }

    export function onShowNextDifference(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's show-next-difference action has been invoked.
        ///     The diff viewer should be navigated to the next difference.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Next Difference action is invoked.
        /// </param>
        _moduleExtension.onShowNextDifference(listener);
    }

    export function onShowPreviousDifference(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's show-previous-difference action has been invoked.
        ///     The diff viewer should be navigated to the previous difference.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Prev Difference action is invoked.
        /// </param>
        _moduleExtension.onShowPreviousDifference(listener);
    }

    export function onNavigateToPosition(listener) {
        /// <summary>
        ///     Adds a listener to be called when the diff extension's set-position action has been invoked.
        ///     The viewer should be navigated to the given position.
        /// </summary>
        /// <param name="listener" type="Function">
        ///     Callback invoked when the Set Position action is invoked.
        /// </param>
        _moduleExtension.onNavigateToPosition(listener);
    }

    export function sendCurrentDiffStatus(prevDiffEnabled, nextDiffEnabled) {
        /// <summary>
        ///     Sends updated diff status information to the TFS host window
        /// </summary>
        /// <param name="prevDiffEnabled" type="Boolean">
        ///     Should the previous difference button be enabled?
        /// </param>
        /// <param name="nextDiffEnabled" type="Boolean">
        ///     Should the next difference button be enabled?
        /// </param>
        _moduleExtension.sendCurrentDiffStatus(prevDiffEnabled, nextDiffEnabled);
    }

    export function sendDiffLines(diffLines: number[]) {
        /// <summary>
        ///     Sends updated diff ranges information to the TFS host window
        /// </summary>
        /// <param name="diffLines" type="number[]">
        ///     The starting line of each diff range
        /// </param>
        _moduleExtension.sendDiffLines(diffLines);
    }

    export function sendEditorDirtyStateChangedEvent(isDirty: boolean) {
        /// <summary>
        ///     Send an editor dirty state change message to the TFS host
        /// </summary>
        _moduleExtension.sendEditorDirtyStateChangedEvent(isDirty);
    }

    export function sendEditorContent(content, requestId) {
        /// <summary>
        ///     Send the specified editor content to the TFS host
        /// </summary>
        /// <param name="content" type="String">
        ///     Current editor content
        /// </param>
        /// <param name="requestId" type="Object">
        ///     Id of the original request (from the Host page) for the
        ///     content. This allows the Host to match up this response
        ///     with its original request.
        /// </param>
        _moduleExtension.sendEditorContent(content, requestId);
    }
}

export module Discussion {
    export var ActionIds = {
        CREATE_THREAD: "discussion-create-thread",
        CREATE_COMMENT: "discussion-create-comment",
        UPDATE_COMMENT: "discussion-update-comment",
        SAVE_COMMENT: "discussion-save-comment",
        DELETE_COMMENT: "discussion-delete-comment",
        SET_SELECTED_THREAD: "discussion-thread-selected",
        CREATE_WORK_ITEM: "discussion-create-work-item",
        GET_WORK_ITEM_TYPES: "discussion-get-work-item-types",
        UPDATE_DISCUSSION: "discussion-update-discussion"
    };
}

export module Adornment {
    export var ActionIds = {
        SET_STATUS: "adornment-set-status"
    };
}

export function getVersionControlExtension() {
    return _moduleExtension;
}

// This instantiates the singleton module extension and initializes XDM communication when the extension is hosted in an iframe
var _moduleExtension = new VersionControlExtension();
_moduleExtension.initializeIframeExtension();
